import { Link } from 'react-router-dom';
import { CollectionInfo } from '@/lib/collections';
import { CollectionStats } from '@/lib/types';
import { ipfsToHttp } from '@/lib/ipfs';

export interface CollectionWithStats extends CollectionInfo {
  stats?: CollectionStats;
  isLoading: boolean;
  sampleImage?: string;
}

export function CollectionCard({ collection }: { collection: CollectionWithStats }) {
  // Prefer official thumbnail over sample NFT, convert IPFS URLs
  const thumbnailUrl = collection.thumbnail ? ipfsToHttp(collection.thumbnail) : '';
  const imageUrl = thumbnailUrl || collection.sampleImage;
  const fallbackUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(collection.name)}&size=300&background=6366f1&color=fff&bold=true`;

  return (
    <Link
      to={`/collection/${collection.slug}`}
      className="group block bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
    >
      <div className="relative h-48">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 to-purple-600 overflow-hidden">
          {imageUrl && (
            <img
              src={imageUrl}
              alt={collection.name}
              className="w-full h-full object-cover opacity-30 group-hover:scale-105 transition-transform duration-300"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
        </div>
        <div className="absolute -bottom-8 left-4 z-10">
          <div className="w-20 h-20 rounded-xl border-4 border-white dark:border-gray-800 shadow-lg overflow-hidden bg-gray-200 dark:bg-gray-700">
            <img
              src={imageUrl || fallbackUrl}
              alt={collection.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).src = fallbackUrl;
              }}
            />
          </div>
        </div>
      </div>

      <div className="pt-12 pb-6 px-4">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{collection.name}</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">{collection.description}</p>

        <div className="flex gap-4 text-sm">
          {collection.isLoading ? (
            <div className="text-gray-400 dark:text-gray-500">Loading stats...</div>
          ) : collection.stats ? (
            <div>
              <span className="text-gray-500 dark:text-gray-400">Items</span>
              <span className="ml-2 font-semibold text-gray-900 dark:text-white">
                {collection.stats.tokenCount.toLocaleString()}
              </span>
            </div>
          ) : collection.totalSupply ? (
            <div>
              <span className="text-gray-500 dark:text-gray-400">Items</span>
              <span className="ml-2 font-semibold text-gray-900 dark:text-white">
                {collection.totalSupply.toLocaleString()}
              </span>
            </div>
          ) : null}
        </div>
      </div>

      <div className="px-4 pb-4">
        <div className="w-full py-2 text-center bg-gray-100 dark:bg-gray-700 dark:text-gray-200 group-hover:bg-blue-600 group-hover:text-white rounded-lg transition-colors font-medium">
          Explore Collection
        </div>
      </div>
    </Link>
  );
}
